import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { PageSkeleton } from '~/components/common/page-skeleton/page-skeleton';

const PromptsPage = lazy(() => import('~/features/configuration/pages/prompts-page'));

/**
 * Selection and filters live in the URL so a prompt is linkable: pasting someone
 * `?ns=…&key=…` opens the prompt they were reading, and a reload keeps the reader's
 * place instead of dropping them back on the empty state.
 *
 * Every field `.catch`es to its default — these values are typed by hand and arrive from
 * links of unknown age, so a stale namespace must degrade to the unfiltered catalog
 * rather than crash the route. `ns`/`key` are free strings because both are catalog
 * identifiers the deployment owns; the page resolves them against the live catalog and
 * ignores what it cannot find.
 */
const searchSchema = z.object({
  /** Selected namespace. Empty = nothing selected. */
  ns: z.string().catch('').default(''),
  /** Selected prompt within `ns`. Empty with a set `ns` = the namespace overview. */
  key: z.string().catch('').default(''),
  /** Free-text filter. */
  q: z.string().catch('').default(''),
  /**
   * Area filter — a business section (Product enrichment, Scoring, …); `all` is unset.
   *
   * Renamed from `agent` when the rail stopped being grouped by the rendering agent. An old
   * `?agent=` link now carries an unknown key, which `validateSearch` drops, so it opens on
   * the unfiltered catalog instead of erroring — which is what `.catch` is here for.
   */
  area: z.string().catch('all').default('all'),
  /** Contents filter; `all` is unset. */
  contents: z.enum(['all', 'prompts', 'templates']).catch('all').default('all'),
  /** True when the reader has collapsed the rail to widen the document. */
  railHidden: z.boolean().catch(false).default(false),
  /** Which pane is showing: the catalog file, or a prompt rendered from a product you supply. */
  pane: z.enum(['catalog', 'render']).catch('catalog').default('catalog'),
  /** Preview panel mode. `source` is the default deliberately — it is what the model receives. */
  mode: z.enum(['source', 'preview', 'compare']).catch('source').default('source'),
  /** Which vertical the preview renders. Empty = the agent's default bucket. */
  bucket: z.string().catch('').default(''),
  /**
   * An enrichment run's `batch_id`, so a reload rejoins a run in progress.
   *
   * In the URL rather than component state because the run outlives the tab: the id is the
   * agent's idempotency key, and the checkpoint behind it is what makes progress readable
   * again after a refresh. Losing the id is what makes a live run unrecoverable.
   */
  run: z.string().catch('').default(''),
});

export type PromptsSearch = z.infer<typeof searchSchema>;

/**
 * Prompts — `/configuration/prompts`. The prompt catalog the running data-scoring agent
 * exposes: namespaces, active versions, and each prompt's text. Permission is inherited
 * from the parent `/configuration` route guard
 * (`requirePermission(Screens.CONFIGURATION)`); the catalog is a property of the
 * deployment rather than of a merchant, so the page ignores the inherited `?platform`
 * param.
 *
 * Not flag-gated: the whole backend chain shipped 2026-08-07 — the agent verifies the
 * user JWT, platform-gateway forwards it on safe methods, and the agents hold the signing
 * key — so the page has real data to render and the `configuration` screen permission is
 * the only access control it needs.
 */
export const Route = createFileRoute('/_authenticated/_tenant/configuration/prompts')({
  validateSearch: searchSchema,
  component: () => (
    <Suspense fallback={<PageSkeleton />}>
      <PromptsPage />
    </Suspense>
  ),
});
